import { logger } from "@/lib/logger";
import { supabase } from "@/lib/supabase";
import type { UserSubscriptionStateRow } from "@/types/userSubscriptionState";

function asNullableString(value: unknown): string | null {
  if (typeof value === "string" && value.trim()) return value;
  return null;
}

function asNullableNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() && !Number.isNaN(Number(value))) {
    return Number(value);
  }
  return null;
}

/**
 * Own subscription mirror row (RLS). Returns null when no row / signed out / error.
 */
export async function fetchUserSubscriptionState(): Promise<UserSubscriptionStateRow | null> {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) return null;

    const { data, error } = await supabase
      .from("user_subscription_state")
      .select("*")
      .maybeSingle();

    if (error) {
      logger.warn("user_subscription_state fetch failed", {
        message: error.message,
        code: error.code,
      });
      return null;
    }

    if (!data) return null;

    const row = data as Record<string, unknown>;

    return {
      user_id: String(row.user_id ?? session.user.id),
      wp_user_id: asNullableNumber(row.wp_user_id),
      wp_subscription_id: asNullableNumber(row.wp_subscription_id),
      tier: asNullableString(row.tier),
      billing_interval: asNullableString(row.billing_interval),
      is_lifetime: row.is_lifetime === true,
      card_status: asNullableString(row.card_status),
      subscription_status: asNullableString(row.subscription_status),
      access_ends_at: asNullableString(row.access_ends_at),
      cancelled_at: asNullableString(row.cancelled_at),
      last_payment_failed_at: asNullableString(row.last_payment_failed_at),
      next_payment_at: asNullableString(row.next_payment_at),
      card_expires_at: asNullableString(row.card_expires_at),
      payment_update_url: asNullableString(row.payment_update_url),
      updated_at: asNullableString(row.updated_at),
    };
  } catch (e) {
    logger.warn("user_subscription_state fetch exception", e);
    return null;
  }
}
